import { useState, useCallback, useMemo, useEffect } from "react";
import { Layers, Download, ArrowLeft, CheckCircle2, Loader2, ChevronDown } from "lucide-react";
import { callAnthropicMessages } from "./anthropicClient";
import { generateTechPackHTML } from "./techPackHTML";
const PRICE_BUSINESS = process.env.REACT_APP_STRIPE_PRICE_BUSINESS || "";
const PRICE_COCKTAIL = process.env.REACT_APP_STRIPE_PRICE_COCKTAIL || "";
const PRICE_BRIDAL   = process.env.REACT_APP_STRIPE_PRICE_BRIDAL   || "";

// Auto-detect pricing tier from design choices
// Gala & Bridal: occasion, or floor/cathedral length, or complex fabric
// Cocktail: cocktail occasion
// Business: business occasion or default
function detectPriceTier(form) {
  const category  = (form.category  || "").toLowerCase();
  const fabricId  = (form.fabricId  || "").toLowerCase();
  const length    = (form.dressLength || "").toLowerCase();
  const cut       = (form.cut        || "").toLowerCase();

  const isBridal  = category === "bridal" || category === "gala";
  const isFloor   = length === "floor" || length === "cathedral" || length === "maxi";
  const isComplex = ["lace","brocade","jacquard","velvet","organza"].includes(fabricId);
  const isBallGown= cut === "ball gown" || cut === "mermaid" || cut === "trumpet";

  if (isBridal || isFloor || isComplex || isBallGown) return "bridal";
  if (category === "cocktail") return "cocktail";
  return "business";
}

function getPriceId(tier) {
  if (tier === "bridal")   return PRICE_BRIDAL;
  if (tier === "cocktail") return PRICE_COCKTAIL;
  return PRICE_BUSINESS;
}

function getTierLabel(tier) {
  if (tier === "bridal")   return "$129";
  if (tier === "cocktail") return "$89";
  return "$69";
}

const C = "#2D2926";

const FABRIC_META = {
  "brocade":        { name:"Brocade",          drape:"structured, holds its own shape",        construction:"Requires sharp scissors and careful cutting along pattern direction. Seam allowances should be wide to prevent raveling. Dry clean only." },
  "chiffon":        { name:"Chiffon",          drape:"fluid, responds to air and movement",     construction:"Use sharp fine needles (size 60/8), pin within seam allowances only. French seams recommended. Hand-finish where possible." },
  "crepe":          { name:"Crepe",            drape:"semi-fluid, controlled and restrained",   construction:"One of the more forgiving fabrics to sew. Holds its shape well during construction. Press with a damp cloth." },
  "georgette":      { name:"Georgette",        drape:"fluid with gentle texture",               construction:"Easier to handle than chiffon due to crinkled texture. Use a fine needle and minimal tension. French seams recommended." },
  "jacquard":       { name:"Jacquard",         drape:"structured with intrinsic pattern",       construction:"Match pattern repeats carefully — this adds yardage. Cut with the pattern direction consistent throughout." },
  "lace":           { name:"Lace",             drape:"semi-structured, romantic and detailed",  construction:"Always line lace — a well-chosen lining in complementary colour is as important as the lace itself. Use fine pins within the pattern." },
  "linen":          { name:"Linen",            drape:"semi-structured, relaxed and honest",     construction:"Pre-wash before cutting — linen shrinks significantly. Crisp pressing is achievable with steam and starch." },
  "organza":        { name:"Organza",          drape:"structured sheer, sculptural volume",     construction:"Organza frays easily — seal all edges immediately after cutting. Self-supporting structure; underlining adds body." },
  "satin":          { name:"Satin",            drape:"semi-fluid, reflective and transforming", construction:"Satin shows every pin hole — pin only within seam allowances. Use a fine needle. Press on the reverse with a pressing cloth." },
  "silk":           { name:"Silk",             drape:"fluid to semi-structured, luminous",      construction:"Handle as little as possible — oils from hands can mark silk. Cut with care and use a sharp rotary cutter where possible." },
  "velvet":         { name:"Velvet",           drape:"fluid-heavy, absorbs and hoards light",   construction:"Always cut pile running in the same direction — typically upward for deeper colour. Use a velvet board for pressing." },
  "wool":           { name:"Wool",             drape:"structured, responds to the tailor",      construction:"Pre-shrink before cutting by steam pressing. Wool tailors beautifully — responds well to shaping with an iron." },
  "deadstock-silk": { name:"Deadstock Silk",   drape:"fluid to semi-structured, storied",       construction:"Yardage is finite and non-replenishable — calculate carefully and cut efficiently. Handle as conventional silk." },
  "organic-cotton": { name:"Organic Cotton",   drape:"semi-structured, honest and clean",       construction:"Pre-wash before cutting. Behaves identically to conventional cotton in construction." },
  "tencel":         { name:"Tencel / Lyocell", drape:"fluid, thoughtful and refined",           construction:"Sews similarly to rayon — use a fine needle and minimal tension. Prone to slight shrinkage; pre-wash recommended." },
  "custom":         { name:"Custom Fabric",    drape:"as specified",                            construction:"Refer to fabric supplier guidance for construction notes." },
};

const GOAL_LABELS = { business:"Business", cocktail:"Cocktail", gala:"Gala / Black Tie", bridal:"Bridal" };

function formatDate() {
  const d = new Date();
  return d.toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" });
}

function generateRef(form) {
  const goal = (form.category || "XX").slice(0,2).toUpperCase();
  const fab  = (form.fabricId || "XX").slice(0,2).toUpperCase();
  const num  = Math.floor(Math.random() * 9000) + 1000;
  return "HH-" + goal + fab + "-" + num;
}

const S = {
  page:  { margin:0, padding:0, minHeight:"100vh", backgroundColor:"#F7F4F1", color:C, fontFamily:"system-ui,sans-serif", fontSize:14, lineHeight:1.4 },
  hdr:   { margin:0, padding:"14px 24px", backgroundColor:"#C9A0A0", borderBottom:"1px solid #b88f8f", display:"flex", alignItems:"center", position:"sticky", top:0, zIndex:50 },
  main:  { margin:"0 auto", padding:"0 20px 80px", maxWidth:720, display:"flex", flexDirection:"column", gap:24 },
  card:  { margin:0, padding:28, backgroundColor:"#fff", borderRadius:24, border:"1px solid #f0eeec", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" },
  lbl:   { fontSize:9, fontWeight:700, letterSpacing:"0.18em", color:"#57534e", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" },
  secHead: { fontSize:10, fontWeight:700, letterSpacing:"0.15em", color:"#57534e", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", paddingBottom:14, marginBottom:18, borderBottom:"1px solid #f5f3f0" },
  row:   { display:"flex", justifyContent:"space-between", alignItems:"baseline", padding:"7px 0", borderBottom:"1px solid #fafaf9" },
  rowLbl:{ fontSize:11, color:"#57534e", fontFamily:"system-ui,sans-serif" },
  rowVal:{ fontSize:12, fontWeight:600, color:C, fontFamily:"system-ui,sans-serif", textAlign:"right" },
};

// Print section wrapper — enforces page break behaviour and strips card chrome
const printSection = { marginBottom:0 };

// ─── Step header ─────────────────────────────────────────────────────────────
function StepHeader({ onReturn }) {
  return (
    <div style={{ backgroundColor:"#fff", borderBottom:"1px solid #e7e5e4", padding:"22px 24px 20px", marginLeft:-20, marginRight:-20, marginBottom:4 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, minWidth:90 }}>
          <div style={{ width:18, height:18, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", backgroundColor:"#f0eeec", border:"1.5px solid #e0dbd4" }}>
            <CheckCircle2 style={{ width:10, height:10, color:"#6b6560" }} />
          </div>
          <span style={{ fontSize:10, color:"#6b6560", fontFamily:"system-ui,sans-serif" }}>The Salon</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:34, height:34, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", backgroundColor:"#C9A0A0", boxShadow:"0 2px 8px rgba(45,41,38,0.22)" }}>
            <span style={{ fontSize:14, fontWeight:700, color:"#2D2926", fontFamily:"system-ui,sans-serif", lineHeight:1 }}>4</span>
          </div>
          <span style={{ fontSize:17, fontWeight:700, color:C, fontFamily:"system-ui,sans-serif", letterSpacing:"-0.02em" }}>The Drawing Room</span>
        </div>
        <div style={{ minWidth:90 }} />
      </div>
      <button onClick={onReturn} style={{ display:"flex", alignItems:"center", gap:7, padding:"8px 14px", borderRadius:999, backgroundColor:"#f5f4f2", border:"1px solid #e7e5e4", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
        <ArrowLeft style={{ width:11, height:11, color:"#78716c" }} />
        <span style={{ fontSize:10, fontWeight:700, color:"#78716c", letterSpacing:"0.12em", textTransform:"uppercase" }}>Return to The Salon</span>
      </button>
    </div>
  );
}

// ─── Adjustments confirmation ─────────────────────────────────────────────────
function AdjustmentsConfirm({ adjustments, onConfirm }) {
  const [checked, setChecked] = useState(function() {
    const init = {};
    adjustments.forEach(function(a, i) { init[i] = true; });
    return init;
  });

  const toggle = function(i) {
    setChecked(function(prev) {
      const next = Object.assign({}, prev);
      next[i] = !next[i];
      return next;
    });
  };

  const confirmed = adjustments.filter(function(_, i) { return checked[i]; });

  if (!adjustments.length) return null;

  return (
    <div style={{ ...S.card, border:"1px solid #e8d8c4", backgroundColor:"#fdf8f3" }}>
      <div style={{ ...S.secHead, borderBottomColor:"#f0e4d0" }}>Confirm Salon Adjustments</div>
      <p style={{ margin:0, marginBottom:16, fontSize:11, color:"#78716c", fontFamily:"system-ui,sans-serif", lineHeight:1.6, fontStyle:"italic" }}>
        These changes were agreed in your Fitting Room session. Confirm which to include in your Tech Pack.
      </p>
      <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:20 }}>
        {adjustments.map(function(adj, i) {
          return (
            <div key={i} onClick={function() { toggle(i); }} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderRadius:12, backgroundColor:checked[i] ? "#fff" : "#f5f0eb", border:"1px solid " + (checked[i] ? "#e0dbd4" : "#ede4d8"), cursor:"pointer", transition:"all 0.15s ease" }}>
              <div style={{ width:18, height:18, borderRadius:5, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", backgroundColor:checked[i] ? "#C9A0A0" : "#e0dbd4", transition:"background-color 0.15s ease" }}>
                {checked[i] && <CheckCircle2 style={{ width:11, height:11, color:"#2D2926" }} />}
              </div>
              <span style={{ fontSize:12, color:checked[i] ? C : "#78716c", fontFamily:"system-ui,sans-serif", fontWeight:checked[i] ? 500 : 400 }}>{adj}</span>
            </div>
          );
        })}
      </div>
      <button onClick={function() { onConfirm(confirmed); }} style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, width:"100%", padding:"14px 0", backgroundColor:"#C9A0A0", color:"#2D2926", fontWeight:700, fontSize:10, letterSpacing:"0.3em", textTransform:"uppercase", borderRadius:12, border:"none", cursor:"pointer", fontFamily:"system-ui,sans-serif", boxShadow:"0 3px 14px rgba(45,41,38,0.2)" }}>
        <CheckCircle2 style={{ width:13, height:13 }} />
        Confirm and Generate Tech Pack
      </button>
    </div>
  );
}

// ─── Tech Pack document view ──────────────────────────────────────────────────
// ─── Drafting Guide Panel ─────────────────────────────────────────────────────
const GUIDE_BG   = "#F0EDE8";
const GUIDE_DARK = "#2D2926";
const GUIDE_MID  = "#57534E";
const GUIDE_MUTED= "#A8A29E";
const GUIDE_LINE = "#E2DDD8";
const ROSE       = "#C9A0A0";

// ─── Pattern Blocks Download Panel ───────────────────────────────────────────
function DraftingGuidePanel({ guide }) {
  const [open, setOpen] = useState(false);

  // ── Parser ────────────────────────────────────────────────────────────────
  // Converts raw markdown-ish AI output into a typed token list.
  // Token types: h1 | h2 | h3 | kv | rule | note | text
  const tokens = useMemo(function() {
    if (!guide) return [];
    const out = [];
    guide.split("\n").forEach(function(raw) {
      const line = raw.trim();
      if (!line) return;

      // Strip leading #s — classify by depth
      if (/^#{1,6}\s/.test(line)) {
        const depth = line.match(/^(#+)/)[1].length;
        const text = line.replace(/^#+\s*/, "").replace(/\*\*/g, "").trim();
        if (!text || text === "---") return;
        if (depth === 1) { out.push({ type: "h1", text }); return; }
        if (depth === 2) { out.push({ type: "h2", text }); return; }
        out.push({ type: "h3", text }); return;
      }

      // Horizontal rules
      if (/^-{2,}$/.test(line) || /^\*{3,}$/.test(line)) {
        out.push({ type: "rule" }); return;
      }

      // Key/value: ALL CAPS key followed by colon + value
      const colonIdx = line.indexOf(":");
      const beforeColon = line.slice(0, colonIdx).trim();
      const isKV = colonIdx > 0 &&
                   colonIdx < 60 &&
                   /^[A-Z][A-Z\s\-\/()"'.]+$/.test(beforeColon) &&
                   !line.startsWith("Note") &&
                   !line.startsWith("If ") &&
                   !line.startsWith("Important");
      if (isKV) {
        const val = line.slice(colonIdx + 1).trim();
        if (val) { out.push({ type: "kv", key: beforeColon, val }); return; }
      }

      // Note / warning lines
      if (/^(Note|Important|Warning|NB)/i.test(line)) {
        out.push({ type: "note", text: line.replace(/^-\s*/, "").replace(/\*\*/g, "") }); return;
      }

      // Plain text
      out.push({ type: "text", text: line.replace(/^[-*]\s*/, "").replace(/\*\*/g, "") });
    });
    return out;
  }, [guide]);

  // ── Render token ──────────────────────────────────────────────────────────
  const renderToken = function(tok, i) {
    switch (tok.type) {

      case "h1":
        return null; // Top-level title suppressed — already in panel header

      case "h2":
        return (
          <div key={i} style={{
            marginTop: 24, marginBottom: 8,
            paddingTop: 18, paddingBottom: 10,
            borderTop: "1px solid " + GUIDE_LINE,
          }}>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: "0.2em",
              color: ROSE, textTransform: "uppercase", fontFamily: "system-ui,sans-serif",
            }}>
              {tok.text.replace(/^\d+[.)]\s*/, "")}
            </span>
          </div>
        );

      case "h3":
        return (
          <div key={i} style={{ marginTop: 14, marginBottom: 6 }}>
            <span style={{
              fontSize: 12, fontWeight: 700, color: GUIDE_DARK,
              fontFamily: "Georgia,serif", letterSpacing: "-0.01em",
            }}>
              {tok.text}
            </span>
          </div>
        );

      case "kv":
        return (
          <div key={i} style={{
            display: "flex", gap: 16, alignItems: "baseline",
            padding: "6px 0", borderBottom: "1px solid " + GUIDE_LINE + "55",
          }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
              color: ROSE, textTransform: "uppercase", fontFamily: "system-ui,sans-serif",
              flexShrink: 0, minWidth: 180,
            }}>
              {tok.key}
            </span>
            <span style={{
              fontSize: 12, fontWeight: 500, color: GUIDE_DARK,
              fontFamily: "'Courier New', monospace", lineHeight: 1.5,
            }}>
              {tok.val}
            </span>
          </div>
        );

      case "rule":
        return <div key={i} style={{ borderTop: "1px solid " + GUIDE_LINE, margin: "14px 0" }} />;

      case "note":
        return (
          <div key={i} style={{
            margin: "10px 0 4px", padding: "8px 12px",
            backgroundColor: ROSE + "12",
            borderLeft: "3px solid " + ROSE,
            borderRadius: "0 8px 8px 0",
          }}>
            <span style={{
              fontSize: 11, color: GUIDE_MID,
              fontFamily: "system-ui,sans-serif", lineHeight: 1.6, fontStyle: "italic",
            }}>
              {tok.text}
            </span>
          </div>
        );

      case "text":
      default:
        return (
          <p key={i} style={{
            margin: "4px 0", fontSize: 12, color: GUIDE_MID,
            fontFamily: "system-ui,sans-serif", lineHeight: 1.65,
          }}>
            {tok.text}
          </p>
        );
    }
  };

  return (
    <div style={{ margin: 0, borderRadius: 24, overflow: "hidden",
      border: "1px solid " + GUIDE_LINE, backgroundColor: GUIDE_BG }}
      className="no-print">

      {/* Header bar */}
      <button onClick={function() { setOpen(!open); }} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "20px 28px", background: "none", border: "none", cursor: "pointer",
        textAlign: "left", gap: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 28, height: 28, borderRadius: "50%",
            backgroundColor: ROSE, display: "flex", alignItems: "center",
            justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#2D2926",
              fontFamily: "system-ui,sans-serif" }}>9</span>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.15em",
              color: GUIDE_MUTED, textTransform: "uppercase",
              fontFamily: "system-ui,sans-serif" }}>For Maker Use</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: GUIDE_DARK,
              fontFamily: "system-ui,sans-serif", letterSpacing: "-0.01em",
              marginTop: 2 }}>Pattern Drafting Notes</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: GUIDE_MUTED,
            fontFamily: "system-ui,sans-serif", fontStyle: "italic" }}>
            {open ? "Collapse" : "Expand"}
          </span>
          <ChevronDown style={{
            width: 16, height: 16, color: GUIDE_MUTED,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }} />
        </div>
      </button>

      {/* Expanded content */}
      {open && (
        <div style={{ borderTop: "1px solid " + GUIDE_LINE }}>

          {/* Intro strip */}
          <div style={{ padding: "14px 28px", backgroundColor: ROSE + "18",
            borderBottom: "1px solid " + GUIDE_LINE }}>
            <p style={{ margin: 0, fontSize: 11, color: GUIDE_MID,
              fontFamily: "system-ui,sans-serif", lineHeight: 1.6, fontStyle: "italic" }}>
              These notes are generated from this client&#8217;s measurements and design specification.
              They provide calculated dimensions, dart placement, and construction guidance
              for a skilled maker to draft from. All measurements are in imperial inches unless noted.
              A muslin is always recommended before cutting the final fabric.
            </p>
          </div>

          {/* Token stream */}
          <div style={{ padding: "8px 28px 24px" }}>
            {tokens.length > 0
              ? tokens.map(renderToken)
              : (
                <div style={{ padding: "24px 0", fontSize: 12, color: GUIDE_MUTED,
                  fontFamily: "system-ui,sans-serif", fontStyle: "italic" }}>
                  Drafting notes are being generated&#8230;
                </div>
              )
            }
          </div>

          {/* Footer */}
          <div style={{ padding: "12px 28px", backgroundColor: GUIDE_DARK,
            borderTop: "1px solid " + GUIDE_LINE }}>
            <p style={{ margin: 0, fontSize: 10, color: "rgba(255,255,255,0.45)",
              fontFamily: "system-ui,sans-serif", lineHeight: 1.5 }}>
              Pattern Drafting Notes are generated from client data and are intended as a
              drafting reference for a trained maker. They do not replace professional
              pattern-making judgement. Complex constructions &#8212; boning, structural underlining,
              bias cut &#8212; should be tested in muslin before cutting final fabric.
              &#169; © 2026 Tabitha Studio. All rights reserved.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// ─── Tech Pack document view ──────────────────────────────────────────────────

// ── PrintableDraftingGuide — always-expanded, no interactivity, for PDF ──────
function PrintableDraftingGuide({ guide }) {
  const tokens = useMemo(function() {
    if (!guide) return [];
    const out = [];
    guide.split("\n").forEach(function(raw) {
      const line = raw.trim();
      if (!line) return;
      if (/^#{1,6}\s/.test(line)) {
        const depth = line.match(/^(#+)/)[1].length;
        const text = line.replace(/^#+\s*/, "").replace(/\*\*/g, "").trim();
        if (!text || text === "---") return;
        if (depth === 1) { out.push({ type: "h1", text }); return; }
        if (depth === 2) { out.push({ type: "h2", text }); return; }
        out.push({ type: "h3", text }); return;
      }
      if (/^-{2,}$/.test(line) || /^\*{3,}$/.test(line)) { out.push({ type: "rule" }); return; }
      const colonIdx = line.indexOf(":");
      const beforeColon = line.slice(0, colonIdx).trim();
      const isKV = colonIdx > 0 && colonIdx < 60 &&
                   /^[A-Z][A-Z\s\-\/()"'.]+$/.test(beforeColon) &&
                   !line.startsWith("Note") && !line.startsWith("If ") && !line.startsWith("Important");
      if (isKV) {
        const val = line.slice(colonIdx + 1).trim();
        if (val) { out.push({ type: "kv", key: beforeColon, val }); return; }
      }
      if (/^(Note|Important|Warning|NB)/i.test(line)) {
        out.push({ type: "note", text: line.replace(/^-\s*/, "").replace(/\*\*/g, "") }); return;
      }
      out.push({ type: "text", text: line.replace(/^[-*]\s*/, "").replace(/\*\*/g, "") });
    });
    return out;
  }, [guide]);

  return (
    <div style={{ padding: "8px 28px 24px" }}>
      {tokens.map(function(tok, i) {
        switch (tok.type) {
          case "h1": return null;
          case "h2": return (
            <div key={i} style={{ marginTop: 24, marginBottom: 8, paddingTop: 18, paddingBottom: 10, borderTop: "1px solid " + GUIDE_LINE }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", color: ROSE, textTransform: "uppercase", fontFamily: "system-ui,sans-serif" }}>
                {tok.text.replace(/^\d+[.)]\s*/, "")}
              </span>
            </div>
          );
          case "h3": return (
            <div key={i} style={{ marginTop: 14, marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: GUIDE_DARK, fontFamily: "Georgia,serif" }}>{tok.text}</span>
            </div>
          );
          case "kv": return (
            <div key={i} style={{ display: "flex", gap: 16, alignItems: "baseline", padding: "6px 0", borderBottom: "1px solid " + GUIDE_LINE + "55" }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: ROSE, textTransform: "uppercase", fontFamily: "system-ui,sans-serif", flexShrink: 0, minWidth: 180 }}>{tok.key}</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: GUIDE_DARK, fontFamily: "'Courier New', monospace", lineHeight: 1.5 }}>{tok.val}</span>
            </div>
          );
          case "rule": return <div key={i} style={{ borderTop: "1px solid " + GUIDE_LINE, margin: "14px 0" }} />;
          case "note": return (
            <div key={i} style={{ margin: "10px 0 4px", padding: "8px 12px", backgroundColor: ROSE + "12", borderLeft: "3px solid " + ROSE, borderRadius: "0 8px 8px 0" }}>
              <span style={{ fontSize: 11, color: GUIDE_MID, fontFamily: "system-ui,sans-serif", lineHeight: 1.6, fontStyle: "italic" }}>{tok.text}</span>
            </div>
          );
          default: return (
            <p key={i} style={{ margin: "4px 0", fontSize: 12, color: GUIDE_MID, fontFamily: "system-ui,sans-serif", lineHeight: 1.65 }}>{tok.text}</p>
          );
        }
      })}
    </div>
  );
}

function TechPackDocument({ form, measurements, observations, adjustments, brief, draftingGuide, designRef, date, modelRender, designRenderBack, fittingRoomRender, fittingRoomRenderBack, lining, liningFabricId, fabricYardage, costEstimate, tier }) {
  const fabric = FABRIC_META[form.fabricId] || FABRIC_META["custom"];
  const goalLabel = GOAL_LABELS[form.category] || (form.category || "");
  const resolvedLining = lining || form.lining;
  const resolvedLiningFabricId = liningFabricId || form.liningFabricId;

  const numFields = [
    { label:"Height",             key:"height",      unit:"in" },
    { label:"Bust",               key:"bust",        unit:"in" },
    { label:"High Bust",          key:"highBust",    unit:"in" },
    { label:"Natural Waist",      key:"waist",       unit:"in" },
    { label:"Hips",               key:"hips",        unit:"in" },
    { label:"Hollow to Hem",      key:"hollowToHem", unit:"in" },
    { label:"Shoulder Width",     key:"shoulder",    unit:"in" },
    { label:"Sleeve Length",      key:"sleeveLength",unit:"in" },
    { label:"Bicep",              key:"bicep",       unit:"in" },
    { label:"Front Waist Length", key:"frontWaist",  unit:"in" },
    { label:"Back Waist Length",  key:"backWaist",   unit:"in" },
    { label:"Waist to Hip",       key:"waistToHip",  unit:"in" },
  ];

  const hasMeasurements = numFields.some(function(f) { return measurements[f.key]; });

  return (
    <div className="tech-pack-document" style={{ display:"flex", flexDirection:"column", gap:24 }}>

      {/* ── Cover strip ── */}
      <div className="print-cover" style={{ ...S.card, backgroundColor:C, border:"none", padding:"32px 36px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
          <div>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.3em", color:"rgba(255,255,255,0.4)", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:8 }}>Tabitha</div>
            <div style={{ fontSize:26, fontWeight:700, color:"#fff", fontFamily:"system-ui,sans-serif", letterSpacing:"-0.02em", lineHeight:1.2 }}>Tech Pack</div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.55)", fontFamily:"system-ui,sans-serif", marginTop:6 }}>{goalLabel} {form.separates ? "Separates" : (form.cut || "Garment")}</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.15em", color:"rgba(255,255,255,0.35)", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:4 }}>Ref</div>
            <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.7)", fontFamily:"system-ui,sans-serif", letterSpacing:"0.08em" }}>{designRef}</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", fontFamily:"system-ui,sans-serif", marginTop:6 }}>{date}</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          {[goalLabel, form.separates ? "Separates" : (form.cut || ""), fabric.name, form.colorDescription || ""].filter(Boolean).map(function(pill, i) {
            return (
              <span key={i} style={{ padding:"4px 12px", borderRadius:999, backgroundColor:"rgba(255,255,255,0.1)", fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.65)", fontFamily:"system-ui,sans-serif", letterSpacing:"0.04em" }}>{pill}</span>
            );
          })}
          <span style={{ padding:"4px 12px", borderRadius:999, display:"inline-flex", alignItems:"center", gap:6, backgroundColor:"rgba(255,255,255,0.1)", fontSize:10, fontWeight:600, fontFamily:"system-ui,sans-serif", letterSpacing:"0.04em" }}>
            <span style={{ width:12, height:12, borderRadius:3, backgroundColor:form.colorHex || "#c9a0a0", display:"inline-block", flexShrink:0, border:"1px solid rgba(255,255,255,0.2)" }} />
            <span style={{ color:"rgba(255,255,255,0.65)" }}>{form.colorHex || ""}</span>
          </span>
        </div>
      </div>

      {/* ── Renders panel ── always shown; placeholders if renders not yet generated */}
      <div className="print-renders print-no-break" style={{ ...S.card, padding:0, overflow:"hidden" }}>
        <div style={{ padding:"20px 28px 16px", borderBottom:"1px solid #f5f3f0" }}>
          <div style={S.secHead}>2 — Design Renders</div>
          <p style={{ margin:0, fontSize:11, color:"#78716c", fontFamily:"system-ui,sans-serif", fontStyle:"italic", lineHeight:1.5 }}>
            Personalised renders from The Salon, generated to your measurements and design choices.
          </p>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0 }}>
          {[
            { src: fittingRoomRender || modelRender,                        label:"Front View" },
            { src: fittingRoomRenderBack || designRenderBack || modelRender, label:"Back View"  },
          ].map(function(view) {
            return (
              <div key={view.label} style={{ position:"relative", backgroundColor:"#1a1714", minHeight:360, borderLeft: view.label === "Back View" ? "1px solid #2a2522" : "none" }}>
                {view.src && view.src !== "PLACEHOLDER" ? (
                  <img
                    src={view.src}
                    alt={view.label}
                    style={{ width:"100%", height:420, objectFit:"cover", objectPosition:"center top", display:"block" }}
                  />
                ) : (
                  <div style={{ width:"100%", height:420, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <span style={{ fontSize:11, color:"rgba(255,255,255,0.2)", fontFamily:"system-ui,sans-serif", fontStyle:"italic" }}>Salon render not generated</span>
                  </div>
                )}
                <div style={{ position:"absolute", bottom:14, left:14, backgroundColor:"rgba(255,255,255,0.9)", backdropFilter:"blur(8px)", padding:"4px 10px", borderRadius:999, fontSize:9, fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", color:C, fontFamily:"system-ui,sans-serif" }}>
                  {view.label}
                </div>
                <div style={{ position:"absolute", bottom:14, right:14, backgroundColor:"rgba(26,23,20,0.55)", backdropFilter:"blur(4px)", padding:"4px 10px", borderRadius:999, fontSize:8, fontWeight:700, letterSpacing:"0.1em", color:"rgba(255,255,255,0.9)", fontFamily:"system-ui,sans-serif" }}>
                  Tabitha
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 1. Design Narrative ── */}
      <div className="print-no-break" style={S.card}>
        <div className="print-sec-head" style={S.secHead}>1 — Design Narrative</div>
        {brief ? (
          <p style={{ margin:0, fontSize:13, color:"#3c3633", lineHeight:1.8, fontFamily:"system-ui,sans-serif", fontStyle:"italic" }}>{brief}</p>
        ) : (
          <p style={{ margin:0, fontSize:12, color:"#78716c", fontStyle:"italic", fontFamily:"system-ui,sans-serif" }}>Generating Design Narrative...</p>
        )}
      </div>

      {/* ── 2. Measurements ── */}
      {hasMeasurements && (
        <div className="print-no-break" style={S.card}>
          <div className="print-sec-head" style={S.secHead}>3 — Measurements</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 32px" }}>
            {numFields.map(function(f) {
              if (!measurements[f.key]) return null;
              return (
                <div key={f.key} style={S.row}>
                  <span style={S.rowLbl}>{f.label}</span>
                  <span style={S.rowVal}>{measurements[f.key]}&Prime;</span>
                </div>
              );
            })}
          </div>
          {(measurements.posture || measurements.fitPreference) && (
            <div style={{ marginTop:18, padding:"12px 16px", backgroundColor:"#fafaf9", borderRadius:12, border:"1px solid #f0eeec" }}>
              {measurements.posture && (
                <div style={{ marginBottom:measurements.fitPreference ? 8 : 0 }}>
                  <span style={{ ...S.lbl, display:"block", marginBottom:4 }}>Posture Note</span>
                  <span style={{ fontSize:11, color:"#57534e", fontFamily:"system-ui,sans-serif" }}>{measurements.posture}</span>
                </div>
              )}
              {measurements.fitPreference && (
                <div>
                  <span style={{ ...S.lbl, display:"block", marginBottom:4 }}>Fit Preference</span>
                  <span style={{ fontSize:11, color:"#57534e", fontFamily:"system-ui,sans-serif" }}>{measurements.fitPreference}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── 3. Construction Specifications ── */}
      <div className="print-break-before" style={S.card}>
        <div className="print-sec-head" style={S.secHead}>4 — Construction Specifications</div>
        {form.separates ? (
          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
            <div>
              <div style={{ ...S.lbl, marginBottom:12, color:"#78716c" }}>Jacket</div>
              <div style={S.row}><span style={S.rowLbl}>Neckline</span><span style={S.rowVal}>{form.jacketNeckline === "Custom" ? form.customJacketNeckline : form.jacketNeckline}</span></div>
              <div style={S.row}><span style={S.rowLbl}>Sleeves</span><span style={S.rowVal}>{form.jacketSleeves === "Custom" ? form.customJacketSleeves : form.jacketSleeves}</span></div>
              {form.jacketFront && <div style={{ marginTop:10 }}><span style={{ ...S.lbl, display:"block", marginBottom:4 }}>Front Detail</span><p style={{ margin:0, fontSize:11, color:"#57534e", lineHeight:1.6, fontFamily:"system-ui,sans-serif" }}>{form.jacketFront}</p></div>}
              {form.jacketBack  && <div style={{ marginTop:10 }}><span style={{ ...S.lbl, display:"block", marginBottom:4 }}>Back Detail</span><p style={{ margin:0, fontSize:11, color:"#57534e", lineHeight:1.6, fontFamily:"system-ui,sans-serif" }}>{form.jacketBack}</p></div>}
            </div>
            <div style={{ borderTop:"1px dashed #e7e5e4", paddingTop:20 }}>
              <div style={{ ...S.lbl, marginBottom:12, color:"#78716c" }}>Skirt</div>
              <div style={S.row}><span style={S.rowLbl}>Type</span><span style={S.rowVal}>{form.skirtType === "Custom" ? form.customSkirtType : form.skirtType}</span></div>
              <div style={S.row}><span style={S.rowLbl}>Length</span><span style={S.rowVal}>{form.skirtLength}</span></div>
              {form.skirtFront && <div style={{ marginTop:10 }}><span style={{ ...S.lbl, display:"block", marginBottom:4 }}>Front Detail</span><p style={{ margin:0, fontSize:11, color:"#57534e", lineHeight:1.6, fontFamily:"system-ui,sans-serif" }}>{form.skirtFront}</p></div>}
              {form.skirtBack  && <div style={{ marginTop:10 }}><span style={{ ...S.lbl, display:"block", marginBottom:4 }}>Back Detail</span><p style={{ margin:0, fontSize:11, color:"#57534e", lineHeight:1.6, fontFamily:"system-ui,sans-serif" }}>{form.skirtBack}</p></div>}
            </div>
          </div>
        ) : (
          <div>
            <div style={S.row}><span style={S.rowLbl}>Silhouette</span><span style={S.rowVal}>{form.cut === "Custom" ? form.customCut : form.cut}</span></div>
            <div style={S.row}><span style={S.rowLbl}>Neckline</span><span style={S.rowVal}>{form.neckline === "Custom" ? form.customNeckline : form.neckline}</span></div>
            <div style={S.row}><span style={S.rowLbl}>Back Neckline</span><span style={S.rowVal}>{form.backNeckline === "Custom" ? form.customBackNeckline : (form.backNeckline || "Closed Back")}</span></div>
            <div style={S.row}><span style={S.rowLbl}>Closure</span><span style={S.rowVal}>{form.closure === "Custom" ? form.customClosure : (form.closure || "Invisible Zip — Centre Back")}</span></div>
            <div style={S.row}><span style={S.rowLbl}>Sleeves</span><span style={S.rowVal}>{form.sleeves === "Custom" ? form.customSleeves : form.sleeves}</span></div>
            {resolvedLining && <div style={S.row}><span style={S.rowLbl}>Lining</span><span style={S.rowVal}>{resolvedLining}</span></div>}
            {resolvedLiningFabricId && resolvedLiningFabricId !== "makers-choice" && resolvedLining && resolvedLining !== "Unlined" && resolvedLining !== "Self-Lined" && (
              <div style={S.row}><span style={S.rowLbl}>Lining Fabric</span><span style={S.rowVal}>
                { resolvedLiningFabricId === "china-silk" ? "China Silk"
                : resolvedLiningFabricId === "silk-habotai" ? "Silk Habotai"
                : resolvedLiningFabricId === "cotton-lawn" ? "Cotton Lawn"
                : resolvedLiningFabricId === "poly-lining" ? "Polyester Lining"
                : resolvedLiningFabricId }
              </span></div>
            )}
            {form.frontDescription && <div style={{ marginTop:14 }}><span style={{ ...S.lbl, display:"block", marginBottom:4 }}>Front Detail</span><p style={{ margin:0, fontSize:11, color:"#57534e", lineHeight:1.65, fontFamily:"system-ui,sans-serif" }}>{form.frontDescription}</p></div>}
            {form.backDescription  && <div style={{ marginTop:14 }}><span style={{ ...S.lbl, display:"block", marginBottom:4 }}>Back Detail</span><p style={{ margin:0, fontSize:11, color:"#57534e", lineHeight:1.65, fontFamily:"system-ui,sans-serif" }}>{form.backDescription}</p></div>}
          </div>
        )}
        {form.atmosphere && (
          <div style={{ marginTop:18, padding:"12px 16px", backgroundColor:"#fafaf9", borderRadius:12, border:"1px solid #f0eeec" }}>
            <span style={{ ...S.lbl, display:"block", marginBottom:4 }}>Setting &amp; Atmosphere</span>
            <p style={{ margin:0, fontSize:11, color:"#57534e", lineHeight:1.65, fontStyle:"italic", fontFamily:"system-ui,sans-serif" }}>{form.atmosphere}</p>
          </div>
        )}
      </div>

      {/* ── 4. Interior Construction Philosophy ── */}
      <div className="print-break-before" style={S.card}>
        <div className="print-sec-head" style={S.secHead}>5 — Interior Construction Philosophy</div>
        <div className="print-philosophy" style={{ marginBottom:20, padding:"14px 16px", backgroundColor:"#fafaf9", borderRadius:12, border:"1px solid #f0eeec" }}>
          <p style={{ margin:0, marginBottom:10, fontSize:12, color:"#3c3633", lineHeight:1.8, fontFamily:"system-ui,sans-serif", fontStyle:"italic", fontWeight:400 }}>
            We believe a garment is built to be worn across a life, not a season.
          </p>
          <p style={{ margin:0, fontSize:11, color:"#57534e", lineHeight:1.85, fontFamily:"system-ui,sans-serif" }}>
            Our interior construction draws on the discipline of 1950s and 1960s couture — an era when a dress was engineered as carefully on the inside as it presented on the outside. Every recommendation below reflects a specific intention: to give the garment a living range of movement, and to ensure that as the wearer changes — in size, in posture, in how they carry themselves — the dress can change with them. These are starting points for conversation between client and maker.
          </p>
        </div>
        {[
          { label:"Waist System",       detail:"Petersham ribbon waistband with 1.5\" adjustment range — three hook-and-eye positions. Exterior silhouette unchanged." },
          { label:"Interior Waist Tie", detail:"Self-fabric tie at centre front and centre back, allowing independent cinch or release of the waist." },
          { label:"Back Bodice",        detail:"Single ease pleat at centre back neck — allows back to expand across shoulders without distorting side seams." },
          { label:"Hem Finish",         detail:"Deep blind hem (1.5–2\" reserve) or faced hem per maker discretion based on fabric weight. Fully finished to the eye." },
          { label:"Lining",              detail: (lining || form.lining || "Fully Lined") === "Fully Lined" ? "Fully lined in complementary fabric. Lining attached at neckline, armholes and hem. Seam allowances enclosed." : (lining || form.lining || "Fully Lined") === "Partially Lined (bodice only)" ? "Bodice fully lined; skirt unlined. Lining attached at waist seam and all bodice edges. Skirt seams finished per fabric." : (lining || form.lining) === "Self-Lined" ? "Self-lined — outer fabric folded and stitched as its own lining. Seam allowances folded in and finished clean." : (lining || form.lining) === "Unlined" ? "Unlined. All interior seams to be finished with French seams, bound edges, or serging. Interior to be presented cleanly." : "Lining approach at maker's discretion. Client has no specific instruction — discuss with maker before cutting." },
        ].map(function(item, i) {
          return (
            <div key={i} style={{ ...S.row, alignItems:"flex-start", paddingTop:10, paddingBottom:10 }}>
              <span style={{ ...S.rowLbl, minWidth:140, flexShrink:0 }}>{item.label}</span>
              <span style={{ fontSize:11, color:"#57534e", fontFamily:"system-ui,sans-serif", lineHeight:1.65, textAlign:"right", flex:1 }}>{item.detail}</span>
            </div>
          );
        })}
      </div>

      {/* ── 5. Fabric Notes ── */}
      <div className="print-no-break" style={S.card}>
        <div className="print-sec-head" style={S.secHead}>6 — Fabric Notes</div>
        <div style={S.row}><span style={S.rowLbl}>Fabric</span><span style={S.rowVal}>{fabric.name}</span></div>
        <div style={S.row}><span style={S.rowLbl}>Drape Character</span><span style={S.rowVal}>{fabric.drape}</span></div>
        {form.colorDescription && <div style={S.row}><span style={S.rowLbl}>Colour</span><span style={{ ...S.rowVal, display:"flex", alignItems:"center", gap:7 }}><span style={{ width:14, height:14, borderRadius:3, backgroundColor:form.colorHex || "#c9a0a0", display:"inline-block", border:"1px solid #e7e5e4" }} />{form.colorDescription}</span></div>}
        <div style={{ marginTop:14, padding:"12px 16px", backgroundColor:"#fafaf9", borderRadius:12, border:"1px solid #f0eeec" }}>
          <span style={{ ...S.lbl, display:"block", marginBottom:6 }}>Construction Notes</span>
          <p style={{ margin:0, fontSize:11, color:"#57534e", lineHeight:1.7, fontFamily:"system-ui,sans-serif" }}>{fabric.construction}</p>
        </div>

        {/* ── Fabric Ordering Summary ── */}
        {(fabricYardage || costEstimate) && (
          <div style={{ marginTop:14, padding:"16px", backgroundColor:"#fdf8f3", borderRadius:12, border:"1px solid #f0e4d0" }}>
            <span style={{ ...S.lbl, display:"block", marginBottom:12, color:"#a07060" }}>Fabric Ordering Summary</span>
            <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
              {fabricYardage && (
                <div style={S.row}>
                  <span style={S.rowLbl}>Estimated Yardage</span>
                  <span style={S.rowVal}>
                    {fabricYardage.tier === "precise"
                      ? fabricYardage.yardage + " yards"
                      : fabricYardage?.yardage?.min + " – " + fabricYardage?.yardage?.max + " yards"}
                  </span>
                </div>
              )}
              {costEstimate && (
                <div style={S.row}>
                  <span style={S.rowLbl}>Estimated Fabric Cost</span>
                  <span style={S.rowVal}>${costEstimate.low} – ${costEstimate.high}</span>
                </div>
              )}
              <div style={{ marginTop:12, paddingTop:10, borderTop:"1px solid #f0e4d0" }}>
                <span style={{ fontSize:10, color:"#78716c", fontFamily:"system-ui,sans-serif", lineHeight:1.6 }}>
                  Fabric cost estimates are referenced against{" "}
                  <a href="https://britexfabrics.com" target="_blank" rel="noopener noreferrer" style={{ color:"#a07060", textDecoration:"none", fontWeight:600 }}>Britex Fabrics</a>
                  {" "}— a wide-ranging source carrying everything from everyday linens to runway silks across multiple price points. Actual costs will vary by supplier, region and availability. Use these figures as a starting point for conversation with your maker.
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── 6. Fit Observations (maker language) ── */}
      {observations && observations.length > 0 && (
        <div className="print-no-break" style={S.card}>
          <div className="print-sec-head" style={S.secHead}>7 — Fit Observations</div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {observations.map(function(ob, i) {
              return (
                <div key={i} style={{ padding:"12px 16px", borderRadius:12, backgroundColor:"#fafaf9", border:"1px solid #f0eeec" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:C, letterSpacing:"0.08em", fontFamily:"system-ui,sans-serif", marginBottom:4 }}>{ob.signal}</div>
                  <p style={{ margin:0, fontSize:11, color:"#57534e", lineHeight:1.65, fontFamily:"system-ui,sans-serif" }}>{ob.technique}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── 7. Agreed Adjustments ── */}
      {adjustments && adjustments.length > 0 && (
        <div className="print-no-break" style={S.card}>
          <div className="print-sec-head" style={S.secHead}>8 — Agreed Adjustments</div>
          <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
            {adjustments.map(function(adj, i) {
              return (
                <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"8px 0", borderBottom:"1px solid #fafaf9" }}>
                  <CheckCircle2 style={{ width:13, height:13, color:"#6aa36a", flexShrink:0, marginTop:1 }} />
                  <span style={{ fontSize:12, color:"#3c3633", fontFamily:"system-ui,sans-serif", lineHeight:1.5 }}>{adj}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── 9. Pattern Drafting Notes ── */}
      {draftingGuide && (
        <DraftingGuidePanel guide={draftingGuide} />
      )}

      {/* ── 9. Pattern Drafting Notes — print version (always expanded) ── */}
      {draftingGuide && (
        <div className="print-only print-drafting" style={{ display: "none", margin: 0, borderRadius: 0, overflow: "hidden",
          border: "none", backgroundColor: "white" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 28px",
            borderBottom: "1px solid " + GUIDE_LINE }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%",
              backgroundColor: ROSE, display: "flex", alignItems: "center",
              justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#2D2926",
                fontFamily: "system-ui,sans-serif" }}>9</span>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.15em",
                color: GUIDE_MUTED, textTransform: "uppercase",
                fontFamily: "system-ui,sans-serif" }}>For Maker Use</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: GUIDE_DARK,
                fontFamily: "system-ui,sans-serif", letterSpacing: "-0.01em",
                marginTop: 2 }}>Pattern Drafting Notes</div>
            </div>
          </div>
          <div style={{ padding: "14px 28px", backgroundColor: ROSE + "18",
            borderBottom: "1px solid " + GUIDE_LINE }}>
            <p style={{ margin: 0, fontSize: 11, color: GUIDE_MID,
              fontFamily: "system-ui,sans-serif", lineHeight: 1.6, fontStyle: "italic" }}>
              These notes are generated from this client&#8217;s measurements and design specification.
              They provide calculated dimensions, dart placement, and construction guidance
              for a skilled maker to draft from. All measurements are in imperial inches unless noted.
              A muslin is always recommended before cutting the final fabric.
            </p>
          </div>
          <PrintableDraftingGuide guide={draftingGuide} />
          <div style={{ padding: "12px 28px", backgroundColor: GUIDE_DARK,
            borderTop: "1px solid " + GUIDE_LINE }}>
            <p style={{ margin: 0, fontSize: 10, color: "rgba(255,255,255,0.45)",
              fontFamily: "system-ui,sans-serif", lineHeight: 1.5 }}>
              Pattern Drafting Notes are generated from client data and are intended as a
              drafting reference for a trained maker. They do not replace professional
              pattern-making judgement. &#169; Tabitha 2026
            </p>
          </div>
        </div>
      )}

      {/* ── 10. Professional Pattern Blocks ── */}
      {/* ── 11. Sign-off ── */}
      <div className="print-signoff" style={{ ...S.card, backgroundColor:"#fafaf9" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:20 }}>
          <div>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"#78716c", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:6 }}>Reference</div>
            <div style={{ fontSize:13, fontWeight:700, color:C, fontFamily:"system-ui,sans-serif", letterSpacing:"0.06em" }}>{designRef}</div>
            <div style={{ fontSize:10, color:"#78716c", fontFamily:"system-ui,sans-serif", marginTop:2 }}>{date}</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"#78716c", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:6 }}>© Tabitha 2026</div>
            <div style={{ fontSize:10, color:"#6b6560", fontFamily:"system-ui,sans-serif", fontStyle:"italic", marginTop:2 }}>tabithastudio.com</div>
          </div>
        </div>
        <div style={{ marginTop:24, paddingTop:20, borderTop:"1px dashed #e7e5e4", display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
          {["Client acknowledgement", "Maker acknowledgement"].map(function(label, i) {
            return (
              <div key={i}>
                <div style={{ fontSize:9, color:"#6b6560", fontFamily:"system-ui,sans-serif", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:20 }}>{label}</div>
                <div style={{ borderBottom:"1px solid #e7e5e4", paddingBottom:4, marginBottom:6 }} />
                <div style={{ fontSize:9, color:"#6b6560", fontFamily:"system-ui,sans-serif" }}>Signature &amp; date</div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}

// ─── Purchase Gate ────────────────────────────────────────────────────────────
function PurchaseGate({ onPurchase, loading, error, tier, user, onSignIn }) {
  const [email,   setEmail]   = useState("");
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [signinError, setSigninError] = useState(null);

  function handleSend() {
    if (!email.trim()) return;
    setSending(true);
    setSigninError(null);
    onSignIn(email.trim())
      .then(function() { setSent(true); setSending(false); })
      .catch(function(err) { setSigninError(err.message || "Something went wrong."); setSending(false); });
  }

  const tierCards = [
    {
      id: "business",
      label: "Business",
      price: "$69",
      note: "Shorter or simpler designs for work, events, and everyday elegance.",
    },
    {
      id: "cocktail",
      label: "Cocktail",
      price: "$89",
      note: "Elevated occasionwear with more styling detail and construction guidance.",
    },
    {
      id: "bridal",
      label: "Gala / Bridal",
      price: "$129",
      note: "Floor-length, statement, or higher-complexity designs requiring the fullest maker brief.",
    },
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }} className="no-print">

      {/* Sample Tech Pack — always visible */}
      <div style={{ backgroundColor:"#fff", borderRadius:20, border:"1px solid #e7e5e4", padding:"22px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:18, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"#a8a29e", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:6 }}>
            Sample Tech Pack
          </div>
          <p style={{ margin:0, fontSize:11, color:"#6b6560", fontFamily:"system-ui,sans-serif", lineHeight:1.65 }}>
            See exactly what you'll receive — narrative, renders, measurements, construction notes, and pattern drafting guide.
          </p>
        </div>
        <a href="/sample-tech-pack.html"
          style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", padding:"12px 18px", borderRadius:12, backgroundColor:"#fafaf9", border:"1px solid #e7e5e4", color:"#6b6560", fontSize:10, fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", textDecoration:"none", whiteSpace:"nowrap" }}>
          View Sample
        </a>
      </div>

      {/* Sign-in wall — shown when not signed in */}
      {!user && (
        <div style={{ backgroundColor:"#fff", borderRadius:20, border:"1px solid #e7e5e4", padding:"32px 28px", display:"flex", flexDirection:"column", alignItems:"center", gap:16, textAlign:"center" }}>
          <div style={{ width:44, height:44, borderRadius:"50%", backgroundColor:"#f5f4f2", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" fill="#C9A0A0"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:"#2D2926", fontFamily:"system-ui,sans-serif", marginBottom:6 }}>Sign in to purchase</div>
            <p style={{ margin:0, fontSize:12, color:"#78716c", fontFamily:"system-ui,sans-serif", lineHeight:1.6, maxWidth:300 }}>
              Your Tech Pack saves to your account so you can download it again at any time.
            </p>
          </div>

          {sent ? (
            <div style={{ padding:"12px 24px", borderRadius:999, backgroundColor:"#f0f7f0", border:"1px solid #c8e0c8", fontSize:11, fontWeight:700, color:"#5a9a5a", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>
              Check your email ✓
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10, width:"100%", maxWidth:320 }}>
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={function(e) { setEmail(e.target.value); setSigninError(null); }}
                onKeyDown={function(e) { if (e.key === "Enter") handleSend(); }}
                style={{ width:"100%", padding:"11px 16px", borderRadius:999, border: signinError ? "1px solid #c0604a" : "1px solid #D8D0C8", fontSize:13, fontFamily:"system-ui,sans-serif", outline:"none", color:"#2D2926", boxSizing:"border-box", textAlign:"center" }}
              />
              {signinError && (
                <div style={{ fontSize:11, color:"#c0604a", fontFamily:"system-ui,sans-serif" }}>{signinError}</div>
              )}
              <button
                onClick={handleSend}
                disabled={sending || !email.trim()}
                style={{ width:"100%", padding:"12px 0", borderRadius:999, border:"none", backgroundColor:"#C9A0A0", color:"#2D2926", fontSize:10, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", cursor: sending ? "default" : "pointer", fontFamily:"system-ui,sans-serif", opacity: sending ? 0.6 : 1 }}
              >
                {sending ? "Sending…" : "Send sign-in link"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Pricing — shown when signed in */}
      {user && (
        <>
          <div style={{ padding:"18px 20px", backgroundColor:"#fdf8f3", borderRadius:18, border:"1px solid #e8d8c4", display:"flex", alignItems:"flex-start", gap:12 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", backgroundColor:"#c8963c", flexShrink:0, marginTop:5 }} />
            <div>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.18em", color:"#a07060", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:6 }}>
                Your Design is Complete
              </div>
              <p style={{ margin:0, fontSize:11, color:"#78716c", fontFamily:"system-ui,sans-serif", lineHeight:1.7, fontStyle:"italic" }}>
                Choose the Tech Pack tier below to download the full maker-ready package — narrative, renders, measurements, construction specifications, interior finishing guidance, and pattern drafting notes.
              </p>
            </div>
          </div>

          {error && (
            <div style={{ padding:"10px 16px", backgroundColor:"#fff5f5", borderRadius:10, border:"1px solid #fde8e8" }}>
              <p style={{ margin:0, fontSize:11, color:"#c0604a", fontFamily:"system-ui,sans-serif" }}>{error}</p>
            </div>
          )}

          <div style={{ display:"grid", gridTemplateColumns:"repeat(3, minmax(0, 1fr))", gap:14 }}>
        {tierCards.map(function(card) {
          const recommended = card.id === tier;
          return (
            <div key={card.id} style={{
              backgroundColor: recommended ? "#eaf3ee" : "#fff",
              borderRadius:20,
              border: recommended ? "1.5px solid #c7d9cf" : "1px solid #e7e5e4",
              padding:"22px 20px",
              display:"flex",
              flexDirection:"column",
              gap:12,
              boxShadow: recommended ? "0 8px 28px rgba(45,41,38,0.08)" : "none",
            }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"#a8a29e", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>
                  {card.label}
                </div>
              </div>

              <div style={{ fontSize:30, fontWeight:700, color:C, fontFamily:"system-ui,sans-serif", letterSpacing:"-0.03em" }}>
                {card.price}
              </div>

              <p style={{ margin:0, minHeight:60, fontSize:11, color:"#6b6560", fontFamily:"system-ui,sans-serif", lineHeight:1.65 }}>
                {card.note}
              </p>

              {card.id === "bridal" && (
                <div style={{ padding:"10px 12px", backgroundColor:"#faf7f4", borderRadius:10, border:"1px solid #e0bdbd" }}>
                  <p style={{ margin:0, fontSize:10, color:"#8d6e63", fontFamily:"system-ui,sans-serif", lineHeight:1.6, fontStyle:"italic" }}>
                    Includes complex fabrics and elaborate maker guidance across designs.
                  </p>
                </div>
              )}

              <button
                onClick={recommended ? function() { onPurchase(); } : undefined}
                disabled={!recommended || loading}
                style={{
                  width:"100%",
                  padding:"12px 0",
                  borderRadius:12,
                  border: recommended ? "none" : "1px solid #e7e5e4",
                  backgroundColor: recommended ? (loading ? "#e7e5e4" : C) : "#fafaf9",
                  color: recommended ? (loading ? "#b8b0a8" : "#fff") : "#b8b0a8",
                  fontSize:10,
                  fontWeight:700,
                  letterSpacing:"0.18em",
                  textTransform:"uppercase",
                  cursor: recommended && !loading ? "pointer" : "not-allowed",
                  fontFamily:"system-ui,sans-serif",
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center",
                  gap:8,
                  marginTop:"auto",
                }}
              >
                {recommended && loading ? <Loader2 style={{ width:13, height:13, animation:"spin 1s linear infinite" }} /> : null}
                {recommended ? (loading ? "Redirecting..." : "Select") : "Select"}
              </button>
            </div>
          );
        })}
      </div>

      <p style={{ margin:0, fontSize:9, color:"#c4bfba", fontFamily:"system-ui,sans-serif", textAlign:"center", letterSpacing:"0.1em" }}>
        Secure payment via Stripe. Your matching tier is highlighted automatically.
      </p>
        </>
      )}

    </div>
  );
}
export default function TechPack({
  form                 = {},
  measurements         = {},
  observations         = [],
  fabricYardage        = null,
  costEstimate         = null,
  consentGiven         = false,
  designRender         = null,
  fittingRoomAdjustments = [],
  designBrief          = "",
  modelRender          = null,
  fittingRoomRender    = null,
  fittingRoomRenderBack = null,
  designRenderBack     = null,
  designSeed           = null,
  onReturnToSalon      = function() {},
  user                 = null,
  onSignIn             = function() {},
  onSignOut            = function() {},
  onNavigate           = function() {},
  onGoHome             = function() {},
  purchaseJustCompleted = false,
  renderJourneyBar     = null,
}) {
  const [stage,        setStage]       = useState("gate");
  const [adjustments,  setAdjustments] = useState([]);
  const [brief,        setBrief]       = useState(designBrief || null);
  const [draftingGuide,setDraftingGuide]= useState(null);
  const [purchased,    setPurchased]   = useState(false);
  const [purchaseTier, setPurchaseTier]= useState(function() { return detectPriceTier(form); });
  // Use designSeed if provided (assigned in Studio at save time), otherwise generate a fallback ref
  const [designRef] = useState(function() {
    return designSeed || generateRef(form);
  });
  const date      = useMemo(function() { return formatDate(); }, []);

  const generateBrief = useCallback(async function(confirmedAdjustments) {
    setAdjustments(confirmedAdjustments);
    setStage("generating");

    const fabric    = (FABRIC_META[form.fabricId] || {}).name || "custom fabric";
    const goal      = GOAL_LABELS[form.category]  || (form.category || "");
    const silhouette= form.cut || "custom silhouette";
    const colour    = form.colorDescription || "a considered colour";
    const atmos     = form.atmosphere || "";
    const front     = form.frontDescription || "";
    const back      = form.backDescription  || "";
    const liningFabricId = String(form.liningFabricId || "").trim();
    const adjList   = confirmedAdjustments.length ? confirmedAdjustments.join(", ") : "";
    const yardageNote = fabricYardage?.tier
      ? ("Yardage estimate: "
        + (fabricYardage.tier === "precise"
          ? (fabricYardage.yardage + " yards")
          : (fabricYardage?.yardage?.min + " to " + fabricYardage?.yardage?.max + " yards"))
        + ".")
      : "";
    const costNote = costEstimate
      ? ("Estimated fabric cost range: $" + costEstimate.low + " to $" + costEstimate.high + ".")
      : "";
    const consentNote = consentGiven
      ? "Measurement consent is confirmed."
      : "Measurement consent has not been confirmed.";

    const prompt = "Write a single editorial paragraph of 3 to 4 sentences for a bespoke garment tech pack in the voice of a semi-luxury fashion house. This is a " + goal + " " + silhouette + " in " + fabric + ", colour " + colour + ". Lining: " + (form.lining || "Fully Lined") + ". " + (atmos ? "Setting: " + atmos + "." : "") + " " + (front ? "Front: " + front + "." : "") + " " + (back ? "Back: " + back + "." : "") + " " + yardageNote + " " + costNote + " " + consentNote + " " + (adjList ? "Fitting room adjustments confirmed: " + adjList + "." : "") + " The tone should be precise, warm and confident — the garment as a considered act of design. Do not use generic phrases like perfect for or ideal for. No bullet points, no headers, no markdown formatting, no bold text, no reference numbers. Just one clean paragraph of plain prose. Where an image of the design is provided, describe what is actually shown — be specific to the render, not generic to the brief.";

    // Build message content — include render image if available so narrative describes the actual result
    const narrativeContent = [];
    if (modelRender && modelRender !== "PLACEHOLDER" && modelRender.startsWith("data:")) {
      const mediaType = modelRender.startsWith("data:image/png") ? "image/png" : "image/jpeg";
      const base64Data = modelRender.split(",")[1];
      narrativeContent.push({ type: "image", source: { type: "base64", media_type: mediaType, data: base64Data } });
    }
    narrativeContent.push({ type: "text", text: prompt });

    try {
      const data = await callAnthropicMessages({
        model: "claude-sonnet-4-5-20250929",
        max_tokens: 1000,
        messages: [{ role: "user", content: narrativeContent }],
      });
      const text = (data.content || []).map(function(b) { return b.text || ""; }).join("");
      setBrief(text || "A considered design, made to measure.");

      // ── Generate drafting guide in parallel ──────────────────────────────
      const m = measurements;
      const hasMeas = m.bust || m.waist || m.hips;
      if (hasMeas) {
        const ease = (form.fabricId || "").includes("silk") || (form.fabricId || "").includes("chiffon") || (form.fabricId || "").includes("georgette")
          ? "minimal ease (0.5\" bodice, 0.75\" hips)"
          : (form.fabricId || "").includes("velvet") || (form.fabricId || "").includes("brocade") || (form.fabricId || "").includes("jacquard")
          ? "standard ease (1\" bodice, 1.25\" hips)"
          : "standard ease (0.75\" bodice, 1\" hips)";

        const draftPrompt = [
          "You are a couture-trained pattern maker generating a Pattern Drafting Notes section for a Tabitha Tech Pack.",
          "Produce precise, calculated drafting instructions for the following made-to-measure garment.",
          "Use imperial inches throughout. Format as clearly labelled sections.",
          "For each measurement calculation, show the formula and result as: Label: formula = result\"",
          "Be specific to this design and these measurements — do not give generic instructions.",
          "IMPORTANT: Always complete every section fully. Never truncate mid-sentence or mid-section. If a section requires more detail, provide it. The response must end with a complete sentence.",
          "",
          "DESIGN:",
          "Goal: " + (GOAL_LABELS[form.category] || form.category || ""),
          "Silhouette: " + (form.cut === "Custom" ? form.customCut : form.cut || ""),
          "Neckline: " + (form.neckline === "Custom" ? form.customNeckline : form.neckline || "not specified"),
          "Back Neckline: " + (form.backNeckline === "Custom" ? form.customBackNeckline : form.backNeckline || "Closed Back"),
          "Closure: " + (form.closure === "Custom" ? form.customClosure : form.closure || "Invisible Zip — Centre Back"),
          "Sleeves: " + (form.sleeves === "Custom" ? form.customSleeves : form.sleeves || "sleeveless"),
          "Fabric: " + fabric,
          "Lining: " + (form.lining || "Fully Lined"),
          liningFabricId && liningFabricId !== "makers-choice" ? "Lining fabric: " + liningFabricId.replace("-", " ") : "",
          "Ease guideline: " + ease,
          "Front detail: " + (front || "not specified"),
          "Back detail: " + (back || "not specified"),
          "",
          "CLIENT MEASUREMENTS:",
          m.bust        ? "Full bust: " + m.bust + "\"" : "",
          m.highBust    ? "High bust: " + m.highBust + "\"" : "",
          m.waist       ? "Natural waist: " + m.waist + "\"" : "",
          m.hips        ? "Hips: " + m.hips + "\"" : "",
          m.hollowToHem ? "Hollow to hem: " + m.hollowToHem + "\"" : "",
          m.height      ? "Height: " + m.height + "\"" : "",
          m.shoulder    ? "Shoulder width: " + m.shoulder + "\"" : "",
          m.sleeveLength? "Sleeve length: " + m.sleeveLength + "\"" : "",
          m.bicep       ? "Bicep: " + m.bicep + "\"" : "",
          m.frontWaist  ? "Front waist length: " + m.frontWaist + "\"" : "",
          m.backWaist   ? "Back waist length: " + m.backWaist + "\"" : "",
          m.waistToHip  ? "Waist to hip: " + m.waistToHip + "\"" : "",
          m.posture     ? "Posture note: " + m.posture : "",
          m.fitPreference?"Fit preference: " + m.fitPreference : "",
          adjList       ? "Fitting room adjustments confirmed: " + adjList : "",
          "",
          "Generate the following sections. Each section should be a clear heading followed by calculated values:",
          "1. Key Working Measurements (quarter-bust, quarter-hip, dart intake, waist suppression per seam)",
          "2. Bodice Block (front and back bodice dimensions, bust dart position and intake, waist dart intake, centre front and back lengths)",
          "3. Skirt or Lower Body (appropriate to the silhouette — sweep, panel widths, length, hem circumference)",
          form.sleeves && form.sleeves !== "Sleeveless" ? "4. Sleeve (cap height, sleeve head width, sleeve length, wrist width)" : "4. Sleeve — Note this design is sleeveless. Include armhole finish guidance only.",
          "5. Construction Notes for This Design (fabric-specific handling, posture adjustments if noted, any fitting room adjustments translated into pattern language, specialist notes for unusual construction elements)",
          "",
          "Important: If the design includes complex elements such as boning, bias cut, cowl draping, or structural underlining, note these explicitly with specific handling guidance. Do not add a closing summary sentence assessing the maker's required skill level.",
          "Do not pad with generic advice. Every line should be specific to this design and these measurements.",
        ].filter(Boolean).join("\n");

        try {
          const draftData = await callAnthropicMessages({
            model: "claude-sonnet-4-5-20250929",
            max_tokens: 4000,
            messages: [{ role: "user", content: draftPrompt }],
          });
          const draftText = (draftData.content || []).map(function(b) { return b.text || ""; }).join("");
          setDraftingGuide(draftText || null);
        } catch (e) {
          // Drafting guide is optional — fail silently
          setDraftingGuide(null);
        }
      }
    } catch (e) {
      setBrief("A considered design, made to measure.");
    } finally {
      setStage("ready");
    }
  }, [form, measurements, fabricYardage, costEstimate, consentGiven]);

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError,   setCheckoutError]   = useState(null);

  useEffect(function() {
    const completed = localStorage.getItem("tabitha_purchase_completed");
    if (!completed) return;
    localStorage.removeItem("tabitha_purchase_completed");
    setPurchased(true);
    setPurchaseTier(detectPriceTier(form));
    if (fittingRoomAdjustments.length) {
      setStage("confirm");
    } else {
      generateBrief([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handlePurchase = useCallback(async function() {
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const tier    = detectPriceTier(form);
      const priceId = getPriceId(tier);
      setPurchaseTier(tier);

      // Save design state so App can restore it after Stripe redirects back
      try {
        localStorage.setItem("tabitha_pending_purchase", JSON.stringify({
          form,
          measurements,
          designSeed:          designRef,
          designRender:        modelRender || designRender,
          designRenderBack,
          fittingRoomRender,
          fittingRoomRenderBack,
        }));
      } catch (storageErr) {
        // localStorage quota exceeded (large images) — continue without saving renders
        try {
          localStorage.setItem("tabitha_pending_purchase", JSON.stringify({ form, measurements, designSeed: designRef }));
        } catch (_) {}
      }

      const res = await fetch("/.netlify/functions/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          priceId,
          successUrl: window.location.origin + "?purchase_success=techpack&design_ref=" + designRef,
          cancelUrl:  window.location.href,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Server error creating checkout session.");
      }

      const { url } = await res.json();
      if (!url) throw new Error("No checkout URL returned.");
      window.location.href = url;
    } catch (err) {
      console.error("Stripe checkout error:", err);
      setCheckoutError("Payment could not be started — please try again.");
      setCheckoutLoading(false);
    }
  }, [designRef, form, measurements, modelRender, designRender, designRenderBack, fittingRoomRender, fittingRoomRenderBack]);

  const handleDownloadHTML = useCallback(async function() {
    const html = generateTechPackHTML({
      form,
      measurements,
      brief,
      draftingGuide,
      observations,
      adjustments,
      designRef,
      date,
      fittingRoomRender,
      fittingRoomRenderBack,
      modelRender: modelRender || designRender,
      designRenderBack,
      fabricYardage,
      costEstimate,
    });
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "Tabitha-Tech-Pack-" + designRef + ".html";
    a.click();
    URL.revokeObjectURL(url);
  }, [form, measurements, brief, draftingGuide, observations, adjustments, designRef, date,
      fittingRoomRender, fittingRoomRenderBack, modelRender, designRender, designRenderBack,
      fabricYardage, costEstimate]);

  return (
    <div style={S.page}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        button:hover { opacity: 0.85; transition: opacity 0.15s ease; }
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }

          body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 2cm 1.8cm; size: A4; }

          /* Strip card chrome for print — white background, no shadows, no rounded corners */
          .tech-pack-document { padding: 0 !important; gap: 0 !important; }
          .tech-pack-document > div {
            background: white !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            padding: 0 !important;
            margin: 0 !important;
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* Cover strip — keep dark background, it prints well */
          .print-cover {
            background: #2D2926 !important;
            color: white !important;
            padding: 28px 0 24px !important;
            margin-bottom: 28pt !important;
            break-after: avoid;
            page-break-after: avoid;
          }

          /* Section heads — Dusty Rose rule line instead of border */
          .print-sec-head {
            font-size: 8pt !important;
            font-weight: 700 !important;
            letter-spacing: 0.2em !important;
            color: #C9A0A0 !important;
            text-transform: uppercase !important;
            border-bottom: 1.5pt solid #C9A0A0 !important;
            padding-bottom: 6pt !important;
            margin-bottom: 12pt !important;
            margin-top: 20pt !important;
          }

          /* Force page breaks BEFORE major sections */
          .print-break-before {
            break-before: page;
            page-break-before: always;
            padding-top: 0 !important;
          }

          /* Avoid breaking these across pages */
          .print-no-break {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* Rows */
          .print-row {
            border-bottom: 0.5pt solid #e7e5e4 !important;
            padding: 5pt 0 !important;
          }

          /* Images */
          img {
            break-inside: avoid;
            page-break-inside: avoid;
            max-width: 100% !important;
            height: auto !important;
          }

          /* Renders grid — two columns, keep together */
          .print-renders {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 20pt !important;
          }

          /* Spacing between spec rows */
          .print-spec-block {
            margin-bottom: 16pt !important;
          }

          /* Interior philosophy box */
          .print-philosophy {
            border-left: 3pt solid #C9A0A0 !important;
            padding-left: 12pt !important;
            margin-bottom: 14pt !important;
            font-style: italic !important;
          }

          /* Sign-off page always on its own page */
          .print-signoff {
            break-before: page;
            page-break-before: always;
          }

          /* Pattern drafting notes — their own section */
          .print-drafting {
            break-before: page;
            page-break-before: always;
          }
        }
      `}</style>

      {/* Sticky nav */}
      <div style={{ ...S.hdr }} className="no-print">
        <button onClick={onGoHome} style={{ display:"flex", alignItems:"center", gap:10, background:"none", border:"none", padding:0, cursor:"pointer", marginRight:16, flexShrink:0 }}>
          <Layers style={{ color:C, width:20, height:20, flexShrink:0 }} />
          <div style={{ textAlign:"left" }}>
            <div style={{ fontSize:17, fontWeight:600, letterSpacing:"-0.02em", fontFamily:"system-ui,sans-serif", color:C }}>Tabitha</div>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.3em", color:"#6f555f", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>The Drawing Room</div>
          </div>
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginLeft:"auto" }}>
          <button onClick={function() { onNavigate("gallery"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>Gallery</button>
          <button onClick={onGoHome} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>New Design</button>
          {user ? (
            <div style={{ width:32, height:32, borderRadius:"50%", border:"2px solid rgba(255,255,255,0.7)", backgroundColor:"rgba(255,255,255,0.8)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}
              onClick={function() { if (onSignOut) onSignOut(); }}>
              <span style={{ fontSize:11, fontWeight:700, color:"#2D2926", fontFamily:"system-ui,sans-serif" }}>{(user.email || "U").slice(0,2).toUpperCase()}</span>
            </div>
          ) : (
            <button onClick={function() { onNavigate("step1"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>Sign In</button>
          )}
          <button onClick={function() { onNavigate("collection"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>My Collection</button>
          <button onClick={function() { onNavigate("contact"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>Contact Us</button>
          {stage === "ready" && (
            <>
              <button onClick={onReturnToSalon} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>← The Salon</button>
              <button onClick={handleDownloadHTML} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:999, backgroundColor:"#2D2926", border:"none", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#fff", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
                <Download style={{ width:10, height:10, color:"#C9A0A0" }} />
                Download
              </button>
            </>
          )}
        </div>
      </div>

      {/* Journey bar — injected from App so it sits below the pink bar */}
      {renderJourneyBar && renderJourneyBar()}

      {/* Step header */}
      <div style={{ maxWidth:720, margin:"0 auto", padding:"0 20px" }} className="no-print">
        <StepHeader onReturn={onReturnToSalon} />
      </div>

      <div style={S.main}>

        {/* Gate stage — shown immediately on entry */}
        {stage === "gate" && (
          <>
            <div className="print-cover" style={{ ...S.card, backgroundColor:C, border:"none", padding:"32px 36px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
                <div>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.3em", color:"rgba(255,255,255,0.4)", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:8 }}>Tabitha</div>
                  <div style={{ fontSize:26, fontWeight:700, color:"#fff", fontFamily:"system-ui,sans-serif", letterSpacing:"-0.02em", lineHeight:1.2 }}>Tech Pack</div>
                  <div style={{ fontSize:13, color:"rgba(255,255,255,0.55)", fontFamily:"system-ui,sans-serif", marginTop:6 }}>{GOAL_LABELS[form.category]} {form.cut}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.15em", color:"rgba(255,255,255,0.35)", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:4 }}>Ref</div>
                  <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.7)", fontFamily:"system-ui,sans-serif", letterSpacing:"0.08em" }}>{designRef}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", fontFamily:"system-ui,sans-serif", marginTop:6 }}>{date}</div>
                </div>
              </div>
            </div>
            <PurchaseGate
              onPurchase={handlePurchase}
              loading={checkoutLoading}
              error={checkoutError}
              tier={detectPriceTier(form)}
              user={user}
              onSignIn={onSignIn}
            />
          </>
        )}

        {/* Adjustments confirmation — after payment if adjustments exist */}
        {stage === "confirm" && (
          <AdjustmentsConfirm
            adjustments={fittingRoomAdjustments}
            onConfirm={generateBrief}
          />
        )}

        {/* Generating — after payment confirmed */}
        {stage === "generating" && (
          <div style={{ ...S.card, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:280, gap:16, textAlign:"center" }}>
            <Loader2 style={{ width:24, height:24, color:"#C9A0A0", animation:"spin 1s linear infinite" }} />
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:C, fontFamily:"system-ui,sans-serif", letterSpacing:"-0.01em", marginBottom:8 }}>
                Preparing your Tech Pack
              </div>
              <div style={{ fontSize:11, color:"#a8a29e", fontFamily:"system-ui,sans-serif", lineHeight:1.7, maxWidth:340, fontStyle:"italic" }}>
                This normally takes 1–2 minutes.
              </div>
            </div>
          </div>
        )}

        {/* Ready — show full doc after purchase + generation */}
        {stage === "ready" && purchased && (
          <>
            <TechPackDocument
              form={form}
              measurements={measurements}
              observations={observations}
              adjustments={adjustments}
              brief={brief}
              draftingGuide={draftingGuide}
              designRef={designRef}
              date={date}
              modelRender={modelRender || designRender}
              designRenderBack={designRenderBack}
              fittingRoomRender={fittingRoomRender}
              fittingRoomRenderBack={fittingRoomRenderBack}
              fabricYardage={fabricYardage}
              costEstimate={costEstimate}
              tier={purchaseTier}
            />

            

            <button onClick={handleDownloadHTML} className="no-print" style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, width:"100%", padding:"18px 0", backgroundColor:"#C9A0A0", color:"#2D2926", fontWeight:700, fontSize:11, letterSpacing:"0.35em", textTransform:"uppercase", borderRadius:14, border:"none", cursor:"pointer", boxShadow:"0 4px 20px rgba(45,41,38,0.22)", fontFamily:"system-ui,sans-serif" }}>
              <Download style={{ width:15, height:15 }} />
              Download as PDF
            </button>

            {/* ── Share panel ── */}
            <div style={{ backgroundColor:"#fff", borderRadius:16, border:"1px solid #e7e5e4", padding:"18px 22px" }} className="no-print">
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"#a8a29e", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:14 }}>Share your design</div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                {/* Copy link */}
                <button
                  onClick={async function() {
                    try {
                      await navigator.clipboard.writeText(window.location.href);
                      alert("Link copied — paste it wherever you'd like to share.");
                    } catch (_) {}
                  }}
                  style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 16px", borderRadius:999, border:"1px solid #E0D9D3", backgroundColor:"#fafaf9", cursor:"pointer", fontSize:10, fontWeight:700, color:C, letterSpacing:"0.1em", fontFamily:"system-ui,sans-serif" }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  Copy link
                </button>
                {/* Email to maker */}
                <a
                  href={"mailto:?subject=My Tabitha Studio Tech Pack — " + designRef + "&body=I've completed my dress design with Tabitha Studio and wanted to share the Tech Pack with you.%0A%0ADesign reference: " + designRef + "%0AView it here: " + encodeURIComponent(window.location.href) + "%0A%0AThe full Tech Pack is attached separately."}
                  style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 16px", borderRadius:999, border:"1px solid #E0D9D3", backgroundColor:"#fafaf9", cursor:"pointer", fontSize:10, fontWeight:700, color:C, letterSpacing:"0.1em", fontFamily:"system-ui,sans-serif", textDecoration:"none" }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  Email to my maker
                </a>
                {/* Native share */}
                <button
                  onClick={async function() {
                    const shareData = {
                      title: "My Tabitha Studio Design — " + designRef,
                      text: "I designed a custom dress with Tabitha Studio. Here's my Tech Pack.",
                      url: window.location.href,
                    };
                    if (navigator.share) {
                      try { await navigator.share(shareData); return; } catch (_) {}
                    }
                    try {
                      await navigator.clipboard.writeText(window.location.href);
                      alert("Link copied — paste it wherever you'd like to share.");
                    } catch (_) {}
                  }}
                  style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 16px", borderRadius:999, border:"none", backgroundColor:C, cursor:"pointer", fontSize:10, fontWeight:700, color:"#fff", letterSpacing:"0.1em", fontFamily:"system-ui,sans-serif" }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                  Share
                </button>
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }} className="no-print">
              <button
                onClick={function() { setPurchased(false); setPurchaseTier(null); }}
                style={{ padding:"12px 0", borderRadius:12, border:"1px solid #e7e5e4", backgroundColor:"#fafaf9", color:"#78716c", fontSize:10, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}
              >
                Download Again
              </button>
              <button
                onClick={onReturnToSalon}
                style={{ padding:"12px 0", borderRadius:12, border:"1px solid #e7e5e4", backgroundColor:"#fafaf9", color:"#78716c", fontSize:10, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}
              >
                Return to The Salon
              </button>
            </div>

            <div style={{ textAlign:"center", fontSize:9, color:"#78716c", letterSpacing:"0.15em", paddingTop:4, fontFamily:"system-ui,sans-serif" }} className="no-print">
              &copy; 2026 Tabitha Studio. All rights reserved.
            </div>
          </>
        )}

      </div>
    </div>
  );
}
